//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  IInstagramCompetitorsLimitResponse,
  IInstagramCompetitorsMediaRequest,
  IInstagramCompetitorsMediaResponse,
  IInstagramCompetitorsSearchResponse,
  IInstagramCompetitorsStatsRequest,
  IInstagramCompetitorsStatsResponse,
} from '@apis/instagram-competitors/instagramCompetitorsApiModel';
import {
  deserializeCompetitorMediaDates,
  toTrackedSourceCompetitor,
} from '@apis/instagram-competitors/instagramCompetitorsApiUtils';
import {
  IInstagramCompetitor,
  ITrackedCompetitor,
} from '@instagram-account/redux/competitors/instagramCompetitorsModel';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

function competitorsFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch(
    'influencers_gateway',
    `/api/v1/competitors${endpoint}`,
    init,
  );
}

/**
 *
 * Gets the client's list of tracked competitors
 *
 * /api/v1/competitors
 *
 * @returns ITrackedCompetitor[]
 */
export async function getCompetitors(
  init?: RequestInit,
): Promise<ITrackedCompetitor[]> {
  const res = await competitorsFetch('', init);
  return await res.json<ITrackedCompetitor[]>();
}

/**
 *
 * Gets the client's tracked competitor count and limit
 *
 * /api/v1/competitors/limit
 *
 * @returns IInstagramCompetitorsLimitResponse
 */
export async function getCompetitorsLimit(): Promise<IInstagramCompetitorsLimitResponse> {
  const res = await competitorsFetch('/limit');
  return await res.json<IInstagramCompetitorsLimitResponse>();
}

/**
 *
 * Gets a competitor via search
 *
 * /api/v1/competitors/search/:username
 *
 * @returns IInstagramCompetitorsSearchResponse
 */
export async function getCompetitorForUsername(
  username: string,
): Promise<IInstagramCompetitorsSearchResponse> {
  const res = await competitorsFetch(`/search/${username}`);
  return await res.json<IInstagramCompetitorsSearchResponse>();
}

/**
 *
 * Removes and adds competitors for the client.
 *
 * /api/v1/competitors/manage
 *
 */
export async function manageCompetitors(
  add: IInstagramCompetitor[],
  remove: IInstagramCompetitor[],
): Promise<Response> {
  const res = await competitorsFetch('/manage', {
    method: 'POST',
    body: JSON.stringify({
      add: add.map(toTrackedSourceCompetitor),
      remove: remove.map(toTrackedSourceCompetitor),
    }),
  });
  return res;
}

/**
 *
 * Gets competitors and stats
 *
 * /api/v1/competitors/stats
 *
 * @returns List of competitors with stats
 */
export async function getCompetitorsWithStats(
  req: IInstagramCompetitorsStatsRequest,
): Promise<IInstagramCompetitorsStatsResponse> {
  const params = makeParams(req);
  const statsRes = await competitorsFetch(`/stats?${params}`);
  return await statsRes.json<IInstagramCompetitorsStatsResponse>();
}

/**
 *
 * Gets competitor media
 *
 * /api/v1/competitors/media
 *
 * @returns List of competitor media
 */
export async function getCompetitorMedia(
  req: IInstagramCompetitorsMediaRequest,
): Promise<IInstagramCompetitorsMediaResponse> {
  const res = await competitorsFetch('/media', {
    method: 'POST',
    body: JSON.stringify({
      ...req,
    }),
  });
  return await res.json<IInstagramCompetitorsMediaResponse>(
    deserializeCompetitorMediaDates,
  );
}

/**
 * Returns the competitor metrics for a client within a given date range as a CSV
 *
 * /api/v1/competitors/stats.csv
 */
export async function getCompetitorMetricsCsv(
  requestParams: IInstagramCompetitorsStatsRequest,
): Promise<string> {
  const params = makeParams(requestParams);
  const res = await competitorsFetch(`/stats.csv?${params}`);
  return res.text();
}
